'use client';

import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import FileUpload from './FileUpload';
import DataQuality from './DataQuality';
import BrandLogo from './BrandLogo';
import CaseServiceDistribution from './CaseServiceDistribution'; // ✅ NEW

type TabType = 'overview' | 'analytics'
  | 'payers-table' | 'payers-charts' | 'upload' | 'data-quality' | 'policy-benefits';

type SectionKey = 'payers';

const PayerDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('policy-benefits');

  const [expandedSections, setExpandedSections] = useState<Record<SectionKey, boolean>>({
    payers: false,
  });

  const toggleSection = (section: SectionKey) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const tabs = [
    {
      id: 'overview' as TabType,
      name: 'Overview',
      icon: (
        <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
      badge: null,
    },
    {
      id: 'analytics' as TabType,
      name: 'Analytics',
      icon: (
        <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      badge: null,
    },
    {
      id: 'data-quality' as TabType,
      name: 'Data Quality',
      icon: (
        <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      badge: null,
    },
    {
      id: 'policy-benefits' as TabType,
      name: 'Policy Benefits',
      icon: (
        <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      badge: null,
    },
    {
      id: 'upload' as TabType,
      name: 'Upload Policies',
      icon: (
        <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
      ),
      badge: null,
    },
  ];

  const collapsibleSections: {
    key: SectionKey;
    label: string;
    color: string;
    iconPath: string;
    subTabs: { id: TabType; label: string; iconPath: string }[];
  }[] = [
    {
      key: 'payers',
      label: 'Payers',
      color: '#10b981',
      iconPath: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
      subTabs: [
        {
          id: 'payers-table',
          label: 'Case & Service Distribution',
          iconPath: 'M3 10h18M3 14h18M10 6h4M10 18h4M3 6h4M3 18h4M17 6h4M17 18h4',
        },
        {
          id: 'payers-charts',
          label: 'Charts',
          iconPath: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
        },
      ],
    },
  ];

  const renderUserInfo = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
      <div style={{ width: '36px', height: '36px', borderRadius: '9px', background: 'linear-gradient(135deg, #10b981, #34d399)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 700, color: 'white', flexShrink: 0 }}>
        {user?.name.charAt(0)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.name}</p>
        <p style={{ fontSize: '11px', color: '#94a3b8', margin: 0 }}>{user?.role}</p>
      </div>
    </div>
  );

  const ComingSoonContent = ({ title, subtitle }: { title: string; subtitle: string; color?: string }) => (
    <div className="flex items-center justify-center min-h-[400px] p-6">
      <div className="text-center p-10 bg-card rounded-2xl border border-border shadow-sm max-w-md w-full space-y-4">
        <div className="w-16 h-16 rounded-2xl bg-primary-50 text-primary-600 flex items-center justify-center mx-auto border border-primary-100 shadow-sm">
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-foreground">{title}</h2>
        <p className="text-xs text-muted-foreground leading-relaxed">{subtitle}</p>
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-700 rounded-xl text-xs font-bold border border-primary-200">
          <span className="w-2 h-2 rounded-full bg-primary-600 animate-pulse" />
          <span>Module Under Active Development</span>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex' }}>

      {/* ══ SIDEBAR ══ */}
      <aside style={{
        width: '260px', flexShrink: 0, background: '#ffffff',
        borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column',
        height: '100vh', position: 'sticky', top: 0,
        boxShadow: '2px 0 8px rgba(0,0,0,0.04)', overflowY: 'auto', overflowX: 'hidden',
      }}>

        {/* Header */}
        <div style={{ padding: '10px', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
          <div style={{ padding: '6px 4px 10px' }}>
            <BrandLogo />
          </div>
          {renderUserInfo()}
        </div>

        {/* Navigation */}
        <nav style={{ flex: 1, padding: '12px 10px', display: 'flex', flexDirection: 'column', gap: '2px' }}>

          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const isUpload = tab.id === 'upload';

            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '11px 14px', borderRadius: '12px', border: 'none',
                  cursor: 'pointer', fontSize: '13px',
                  fontWeight: isActive || isUpload ? 600 : 500,
                  transition: 'all 0.18s', textAlign: 'left',
                  background: isUpload
                    ? 'linear-gradient(135deg, #22c55e, #10b981)'
                    : isActive
                      ? 'linear-gradient(135deg, #10b981, #34d399)'
                      : 'transparent',
                  color: isUpload || isActive ? 'white' : '#64748b',
                  boxShadow: isUpload
                    ? '0 4px 14px rgba(34,197,94,0.35)'
                    : isActive
                      ? '0 4px 14px rgba(16,185,129,0.25)'
                      : 'none',
                }}
                onMouseEnter={(e) => {
                  if (!isActive && !isUpload) { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#0f172a'; }
                  else if (isUpload) { e.currentTarget.style.boxShadow = '0 6px 20px rgba(34,197,94,0.45)'; e.currentTarget.style.transform = 'translateY(-1px)'; }
                }}
                onMouseLeave={(e) => {
                  if (!isActive && !isUpload) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#64748b'; }
                  else if (isUpload) { e.currentTarget.style.boxShadow = '0 4px 14px rgba(34,197,94,0.35)'; e.currentTarget.style.transform = 'translateY(0)'; }
                }}
              >
                <div style={{ flexShrink: 0 }}>{tab.icon}</div>
                <span style={{ flex: 1 }}>{tab.name}</span>
                {tab.badge && (
                  <span style={{ padding: '2px 8px', borderRadius: '999px', fontSize: '10px', fontWeight: 700, background: isActive ? 'rgba(255,255,255,0.2)' : '#f0fdf4', color: isActive ? 'white' : '#10b981' }}>
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}

          <div style={{ margin: '10px 6px', borderTop: '1px solid #f1f5f9' }} />

          {collapsibleSections.map((section) => {
            const isExpanded = expandedSections[section.key];
            const isSectionActive = activeTab.startsWith(section.key);

            return (
              <div key={section.key}>
                <button
                  onClick={() => toggleSection(section.key)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '11px 14px', borderRadius: '12px', border: 'none',
                    cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                    transition: 'all 0.18s', textAlign: 'left',
                    background: isSectionActive ? `${section.color}14` : isExpanded ? '#f8fafc' : 'transparent',
                    color: isSectionActive ? section.color : '#374151',
                  }}
                  onMouseEnter={(e) => { if (!isSectionActive) e.currentTarget.style.background = '#f1f5f9'; }}
                  onMouseLeave={(e) => { if (!isSectionActive) e.currentTarget.style.background = isSectionActive ? `${section.color}14` : isExpanded ? '#f8fafc' : 'transparent'; }}
                >
                  <div style={{ width: '18px', height: '18px', flexShrink: 0 }}>
                    <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d={section.iconPath} />
                    </svg>
                  </div>
                  <span style={{ flex: 1 }}>{section.label}</span>
                  <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="#9ca3af" strokeWidth={2.5}
                    style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease', flexShrink: 0 }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                {isExpanded && (
                  <div style={{ paddingLeft: '14px', marginTop: '2px', marginBottom: '2px' }}>
                    <div style={{ borderLeft: `2px solid ${section.color}25`, paddingLeft: '12px', display: 'flex', flexDirection: 'column', gap: '1px' }}>
                      {section.subTabs.map((sub) => {
                        const isSubActive = activeTab === sub.id;
                        return (
                          <button
                            key={sub.id}
                            onClick={() => setActiveTab(sub.id)}
                            style={{
                              width: '100%', display: 'flex', alignItems: 'center', gap: '8px',
                              padding: '9px 12px', borderRadius: '9px', border: 'none',
                              cursor: 'pointer', fontSize: '12.5px',
                              fontWeight: isSubActive ? 600 : 400,
                              transition: 'all 0.15s', textAlign: 'left',
                              background: isSubActive ? section.color : 'transparent',
                              color: isSubActive ? '#ffffff' : '#6b7280',
                              boxShadow: isSubActive ? `0 3px 10px ${section.color}33` : 'none',
                            }}
                            onMouseEnter={(e) => { if (!isSubActive) { e.currentTarget.style.background = `${section.color}12`; e.currentTarget.style.color = section.color; } }}
                            onMouseLeave={(e) => { if (!isSubActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#6b7280'; } }}
                          >
                            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={isSubActive ? 2.2 : 1.8} style={{ flexShrink: 0 }}>
                              <path strokeLinecap="round" strokeLinejoin="round" d={sub.iconPath} />
                            </svg>
                            <span>{sub.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Footer — Logout */}
        <div style={{ padding: '14px', borderTop: '1px solid #f1f5f9', flexShrink: 0 }}>
          <button
            onClick={logout}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              padding: '11px 16px', background: '#fef2f2', border: '1px solid #fecaca',
              color: '#ef4444', fontWeight: 600, fontSize: '13px',
              borderRadius: '12px', cursor: 'pointer', transition: 'all 0.18s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#fef2f2'; }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" x2="9" y1="12" y2="12" />
            </svg>
            Sign Out
          </button>
        </div>
      </aside>

      {/* ══ MAIN CONTENT ══ */}
      <main style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '28px' }}>

          {/* Overview */}
          {activeTab === 'overview' && (
            <div>
              <div style={{ marginBottom: '24px' }}>
                <h1 style={{ fontSize: '26px', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px', margin: '0 0 4px' }}>Payer Dashboard</h1>
                <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>Payer analytics & insights</p>
              </div>
              <ComingSoonContent title="Overview Coming Soon" subtitle="Payer overview data will appear here." color="#10b981" />
            </div>
          )}

          {/* Analytics */}
          {activeTab === 'analytics' && (
            <ComingSoonContent title="Analytics Coming Soon" subtitle="Payer analytics will appear here." color="#10b981" />
          )}

          {/* ✅ Policy Benefits — dedicated view for uploaded policies */}
          {activeTab === 'policy-benefits' && (
            <div style={{ background: '#ffffff', borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <div style={{ padding: '24px 32px', background: 'linear-gradient(135deg, #f0f9ff, #e0f2fe)', borderBottom: '1px solid #bae6fd', display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '48px', height: '48px', background: 'linear-gradient(135deg, #0ea5e9, #38bdf8)', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(14,165,233,0.2)' }}>
                  <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1e293b', margin: 0 }}>Policy Benefits</h2>
                  <p style={{ fontSize: '13px', color: '#0284c7', margin: 0, marginTop: '2px' }}>View uploaded insurance policy plans, networks and benefit limits</p>
                </div>
              </div>
              <CaseServiceDistribution forcePolicyView={true} />
            </div>
          )}

          {/* ✅ Case & Service Distribution — TanStack Table + Charts */}
          {activeTab === 'payers-table' && (
            <div style={{ background: '#ffffff', borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <div style={{ padding: '24px 32px', background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)', borderBottom: '1px solid #a7f3d0', display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '48px', height: '48px', background: 'linear-gradient(135deg, #10b981, #34d399)', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(16,185,129,0.2)' }}>
                  <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                  </svg>
                </div>
                <div>
                  <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1e293b', margin: 0 }}>Case & Service Distribution</h2>
                  <p style={{ fontSize: '13px', color: '#059669', margin: 0, marginTop: '2px' }}>KPIs · Charts · Provider table with search & sort</p>
                </div>
              </div>
              <CaseServiceDistribution forcePolicyView={true} />
            </div>
          )}

          {/* Payers Charts — Coming Soon */}
          {activeTab === 'payers-charts' && (
            <div style={{ background: '#ffffff', borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <div style={{ padding: '24px 32px', background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)', borderBottom: '1px solid #a7f3d0', display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '48px', height: '48px', background: 'linear-gradient(135deg, #10b981, #34d399)', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(16,185,129,0.2)' }}>
                  <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div>
                  <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1e293b', margin: 0 }}>Payers — Charts</h2>
                  <p style={{ fontSize: '13px', color: '#059669', margin: 0, marginTop: '2px' }}>Visual payer analytics</p>
                </div>
              </div>
              <div style={{ padding: '32px' }}>
                <ComingSoonContent title="Charts Coming Soon" subtitle="Payer charts will appear here." color="#10b981" />
              </div>
            </div>
          )}

          {/* Data Quality */}
          {activeTab === 'data-quality' && (
            <div style={{ padding: '32px' }}>
              <DataQuality />
            </div>
          )}

          {/* Upload */}
          {activeTab === 'upload' && (
            <div style={{ background: '#ffffff', borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <div style={{ padding: '32px' }}>
                <FileUpload
                  defaultMode="policies"
                  onUploadComplete={(mode) => {
                    if (mode === 'policies') {
                      setActiveTab('policy-benefits');
                    } else {
                      setActiveTab('overview');
                    }
                  }}
                />
              </div>
            </div>
          )}

        </div>
      </main>
    </div>
  );
};

export default PayerDashboard;